/**
 * Job Polling Utility
 *
 * Polls the generic /jobs/{job_id} endpoint until job reaches terminal state.
 * Works with worker-mode execution where jobs go: queued → running → completed/failed
 *
 * CRITICAL: Uses /jobs/{job_id} (NO /api prefix), NOT flow-specific endpoints like /ingest/jobs/{job_id}/status
 */

const DEFAULT_POLL_INTERVAL = 1000 // 1 second
const DEFAULT_MAX_WAIT_SECONDS = 600 // 10 minutes

/**
 * Terminal job statuses (execution complete)
 * IMPORTANT: API returns lowercase status values
 */
const TERMINAL_STATUSES = ['completed', 'failed', 'canceled', 'cancelled']

/**
 * Success statuses
 */
const SUCCESS_STATUSES = ['completed']

/**
 * Failure statuses
 */
const FAILURE_STATUSES = ['failed', 'canceled', 'cancelled']

/**
 * Poll a job until it reaches a terminal state (completed, failed, canceled)
 *
 * @param {string} jobId - The job ID to poll
 * @param {string} apiBaseUrl - API base URL (e.g., http://localhost:8000)
 * @param {Object} options - Polling options
 * @param {number} options.maxWaitSeconds - Maximum time to wait (default: 600 = 10 minutes)
 * @param {number} options.pollInterval - Polling interval in ms (default: 1000 = 1 second)
 * @param {Function} options.onStatusChange - Callback when status changes (job) => void
 * @param {Function} options.onProgress - Callback for progress updates (progress, message) => void
 * @param {AbortSignal} options.signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Resolved job object when completed
 * @throws {Error} - If job failed, canceled, or timeout
 */
export async function pollJobUntilComplete(jobId, apiBaseUrl, options = {}) {
  const {
    maxWaitSeconds = DEFAULT_MAX_WAIT_SECONDS,
    pollInterval = DEFAULT_POLL_INTERVAL,
    onStatusChange = null,
    onProgress = null,
    signal = null,
  } = options

  const startTime = Date.now()
  let lastStatus = null
  let attempts = 0

  const pollUrl = `${apiBaseUrl}/jobs/${jobId}`
  console.log(`[JobPoller] Starting poll for job ${jobId}`)
  console.log(`[JobPoller] Polling URL: ${pollUrl}`)

  while (true) {
    // Check for abort
    if (signal?.aborted) {
      throw new Error('Execution cancelled')
    }

    // Check timeout
    const elapsedSeconds = (Date.now() - startTime) / 1000
    if (elapsedSeconds > maxWaitSeconds) {
      const stuckHint = (lastStatus === 'queued')
        ? ' Worker is not consuming jobs — check that the worker process is running.'
        : ''
      throw new Error(
        `Job timeout: Job ${jobId} did not complete after ${maxWaitSeconds} seconds. ` +
        `Last status: ${lastStatus || 'UNKNOWN'}.${stuckHint}`
      )
    }

    attempts++

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('modernizeit_token')
      const headers = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Fetch job status from generic jobs endpoint (NO /api prefix)
      const response = await fetch(pollUrl, {
        headers,
        signal
      })

      if (!response.ok) {
        // Don't throw on 404 - job might not be in DB yet (race condition)
        if (response.status === 404 && attempts < 5) {
          console.log(`[JobPoller] Job ${jobId} not found yet (attempt ${attempts}/5), retrying...`)
          await sleep(pollInterval, signal)
          continue
        }

        // Don't throw on 401/403 - auth/access might not be ready yet
        if ((response.status === 401 || response.status === 403) && attempts < 5) {
          console.warn(`[JobPoller] Auth error ${response.status} for job ${jobId} (attempt ${attempts}/5), retrying...`)
          await sleep(pollInterval, signal)
          continue
        }

        const errorText = await response.text()
        throw new Error(`Job poll failed (${response.status}): ${errorText}`)
      }

      const job = await response.json()
      const currentStatus = job.status

      if (!currentStatus) {
        throw new Error(`Job ${jobId} has no status field`)
      }

      // Log status changes
      if (currentStatus !== lastStatus) {
        console.log(`[JobPoller] Job ${jobId}: ${lastStatus || 'UNKNOWN'} → ${currentStatus}`)
        lastStatus = currentStatus

        // Call status change callback
        if (onStatusChange) {
          onStatusChange(job)
        }

        // Call progress callback with mapped messages
        if (onProgress) {
          const message = getStatusMessage(currentStatus)
          const progress = getStatusProgress(currentStatus)
          onProgress(progress, message)
        }
      }

      // Check for SUCCESS terminal state
      if (SUCCESS_STATUSES.includes(currentStatus)) {
        console.log(`[JobPoller] Job ${jobId} completed successfully`)
        return job
      }

      // Check for FAILURE terminal states
      if (FAILURE_STATUSES.includes(currentStatus)) {
        const errorMsg = job.error_message || job.error || 'Job failed without error message'
        throw new Error(`Job failed: ${errorMsg}`)
      }

      // Still processing (queued or running)
      await sleep(pollInterval, signal)

    } catch (error) {
      // Re-throw if it's an abort or explicit failure
      if (error.name === 'AbortError' || error.message.includes('Job failed') || error.message.includes('Job timeout')) {
        throw error
      }

      // Don't throw on network errors immediately - keep polling
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        console.warn(`[JobPoller] Network error polling job ${jobId}, retrying...`)
        await sleep(pollInterval, signal)
        continue
      }

      // Throw for actual job failures
      throw error
    }
  }
}

/**
 * Get human-readable message for job status
 */
function getStatusMessage(status) {
  const messages = {
    queued: 'Waiting for worker...',
    running: 'Processing...',
    completed: 'Complete',
    failed: 'Failed',
    canceled: 'Canceled',
    cancelled: 'Canceled',
  }
  return messages[status] || status
}

/**
 * Get progress percentage for job status
 */
function getStatusProgress(status) {
  const progress = {
    queued: 10,
    running: 50,
    completed: 100,
    failed: 0,
    canceled: 0,
    cancelled: 0,
  }
  return progress[status] || 0
}

/**
 * Sleep helper (abortable)
 */
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Execution cancelled'))
      return
    }

    const timeout = setTimeout(resolve, ms)

    signal?.addEventListener('abort', () => {
      clearTimeout(timeout)
      reject(new Error('Execution cancelled'))
    }, { once: true })
  })
}

/**
 * Convenience function to poll multiple jobs in parallel
 *
 * @param {string[]} jobIds - Array of job IDs to poll
 * @param {string} apiBaseUrl - API base URL
 * @param {Object} options - Polling options (same as pollJobUntilComplete)
 * @returns {Promise<Object[]>} - Array of completed jobs
 */
export async function pollJobsUntilComplete(jobIds, apiBaseUrl, options = {}) {
  return Promise.all(
    jobIds.map(jobId => pollJobUntilComplete(jobId, apiBaseUrl, options))
  )
}

export default {
  pollJobUntilComplete,
  pollJobsUntilComplete,
  TERMINAL_STATUSES,
  SUCCESS_STATUSES,
  FAILURE_STATUSES,
}
